import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
// ⬇️ usar el autocomplete externo con HMAC
import ExtAddressAutocomplete from "components/extAddressAutocomplete";
import React, { useState, useEffect } from "react";
import parametersExternalController from "../../../service/External/ParametersExternalController.ts";

const ExternalBusinessInformation = ({
  formData,
  handleInputChange,
  errors,
  validateWebsiteField,
  setErrors,
  setShowBusinessAddressModal,
  setBusinessAddressToEdit,
  handleEditBusinessAddress,
  handleDeleteBusinessAddress,
}) => {
  const [businessStructureOptions, setBusinessStructureOptions] = useState([]);

  useEffect(() => {
    const loadStructures = async () => {
      try {
        const response = await parametersExternalController
          .getBusinessStructures()
          .catch(() => ({ data: [] }));
        const structures = response?.data || [];
        setBusinessStructureOptions(
          (structures || []).map((s) => ({
            value: s.value || s.name || s,
            label: s.label || s.name || s,
          }))
        );
      } catch (e) {
        setBusinessStructureOptions([]);
      }
    };
    loadStructures();
  }, []);

  return (
    <div>
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">
            <i className="ni ni-briefcase-24 mr-2" />
            Business Information
          </h5>
        </CardHeader>
        <CardBody>
          {/* Revenue and Subscribers Section */}
          <h6 className="mb-3">Business Metrics *</h6>
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="totalSubscribers"
                  className="form-control-label"
                >
                  Total Subscribers *
                </Label>
                <Input
                  id="totalSubscribers"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={formData.totalSubscribers}
                  onChange={(e) =>
                    handleInputChange("totalSubscribers", e.target.value)
                  }
                  maxLength={10}
                  invalid={
                    formData.totalSubscribers &&
                    parseFloat(formData.totalSubscribers) <= 0
                  }
                  bsSize="sm"
                />
                {formData.totalSubscribers &&
                  parseFloat(formData.totalSubscribers) <= 0 && (
                    <div className="invalid-feedback d-block">
                      Total subscribers must be greater than 0
                    </div>
                  )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="totalMonthlyRevenue"
                  className="form-control-label"
                >
                  Total Monthly Revenue *
                </Label>
                <Input
                  id="totalMonthlyRevenue"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={formData.totalMonthlyRevenue}
                  onChange={(e) =>
                    handleInputChange("totalMonthlyRevenue", e.target.value)
                  }
                  maxLength={18}
                  invalid={
                    formData.totalMonthlyRevenue &&
                    parseFloat(formData.totalMonthlyRevenue) <= 0
                  }
                  bsSize="sm"
                />
                {formData.totalMonthlyRevenue &&
                  parseFloat(formData.totalMonthlyRevenue) <= 0 && (
                    <div className="invalid-feedback d-block">
                      Total monthly revenue must be greater than 0
                    </div>
                  )}
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-3" />
          <h6 className="mb-3">Business Details</h6>
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="legalName" className="form-control-label">
                  Legal Name *
                </Label>
                <Input
                  id="legalName"
                  type="text"
                  value={formData.legalName}
                  onChange={(e) =>
                    handleInputChange("legalName", e.target.value)
                  }
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="dbaName" className="form-control-label">
                  DBA Name *
                </Label>
                <Input
                  id="dbaName"
                  type="text"
                  value={formData.dbaName}
                  onChange={(e) => handleInputChange("dbaName", e.target.value)}
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="federalTaxId" className="form-control-label">
                  Federal Tax ID *
                </Label>
                <Input
                  id="federalTaxId"
                  type="text"
                  value={formData.federalTaxId}
                  onChange={(e) =>
                    handleInputChange("federalTaxId", e.target.value)
                  }
                  placeholder="XX-XXXXXXX"
                  maxLength={10}
                  pattern="\d{2}-\d{7}"
                  required
                  bsSize="sm"
                  className={errors.federalTaxId ? "is-invalid" : ""}
                />
                {errors.federalTaxId && (
                  <div className="invalid-feedback d-block">
                    {errors.federalTaxId}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="website" className="form-control-label">
                  Website (Optional)
                </Label>
                <Input
                  id="website"
                  type="text"
                  value={formData.website}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange("website", value);
                    const error = validateWebsiteField(value);
                    setErrors((prev) => ({
                      ...prev,
                      website: error,
                    }));
                  }}
                  placeholder="www.example.com"
                  className={errors.website ? "is-invalid" : ""}
                  bsSize="sm"
                />
                {errors.website && (
                  <div className="invalid-feedback d-block">
                    {errors.website}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label htmlFor="stateOfInc" className="form-control-label">
                  State Of Formation *
                </Label>
                <ExtAddressAutocomplete
                  value={formData.stateOfInc}
                  onChange={(text) => {
                    handleInputChange("stateOfInc", (text || "").slice(0, 255));
                  }}
                  onSelect={(item) => {
                    const details = item?.details || {};
                    const onlyState = details.state || "";
                    handleInputChange("stateOfInc", onlyState);
                  }}
                  placeholder="Start typing state..."
                  maxLength={255}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label htmlFor="dateEstablished" className="form-control-label">
                  Date Established *
                </Label>
                <Input
                  id="dateEstablished"
                  type="date"
                  value={formData.dateEstablished}
                  onChange={(e) =>
                    handleInputChange("dateEstablished", e.target.value)
                  }
                  max={new Date().toISOString().split("T")[0]}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="businessPhoneNumber"
                  className="form-control-label"
                >
                  Phone Number *
                </Label>
                <Input
                  id="businessPhoneNumber"
                  type="tel"
                  value={formData.businessPhoneNumber}
                  onChange={(e) =>
                    handleInputChange("businessPhoneNumber", e.target.value)
                  }
                  placeholder="Enter phone number"
                  maxLength={20}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label
                  htmlFor="businessStructure"
                  className="form-control-label"
                >
                  Business Structure *
                </Label>
                <Input
                  id="businessStructure"
                  type="select"
                  value={formData.businessStructure}
                  onChange={(e) =>
                    handleInputChange("businessStructure", e.target.value)
                  }
                  bsSize="sm"
                >
                  <option value="">Select Business Structure</option>
                  {businessStructureOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Business Addresses Section */}
      <Card className="mb-3">
        <CardHeader>
          <h5 className="mb-0">
            <i className="ni ni-pin-3 mr-2" />
            Business Addresses *
          </h5>
          <p className="text-sm text-muted mb-0">
            At least one business address required
          </p>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setBusinessAddressToEdit(null);
                setShowBusinessAddressModal(true);
              }}
            >
              <i className="ni ni-fat-add mr-2" />
              Add Business Address
            </Button>
            <small className="text-muted">
              You can add multiple business addresses
            </small>
          </div>

          {formData.businessAddresses.length === 0 ? (
            <p className="text-muted mb-0">No business addresses added yet.</p>
          ) : (
            formData.businessAddresses.map((address, index) => (
              <div key={address.id} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{address.addressType}</h6>
                    <p className="mb-0 text-muted text-sm">
                      {[
                        address.streetAddress,
                        address.city,
                        address.state,
                        address.zipCode,
                      ]
                        .filter((field) => field && field.trim() !== "")
                        .join(", ")}
                    </p>
                    {address.phoneNumber && (
                      <p className="mb-0 text-muted text-sm">
                        <strong>Phone:</strong> {address.phoneNumber}
                      </p>
                    )}
                  </div>
                  <div className="btn-group">
                    <Button
                      color="primary"
                      size="md"
                      title="Edit business address"
                      onClick={() => handleEditBusinessAddress(address)}
                    >
                      <i className="ni ni-settings-gear-65" />
                    </Button>
                    <Button
                      color="secondary"
                      size="md"
                      title="Remove business address"
                      onClick={() => handleDeleteBusinessAddress(address.id)}
                    >
                      <i className="ni ni-fat-remove" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ExternalBusinessInformation;